/**
 * Public checkout summary — the "cart" page users land on after clicking
 * "Choose & pay" on /prices.
 *
 * Flow (auth-aware):
 *   • Logged out → shows summary + "Create account & pay" and "Sign in & pay"
 *     buttons. Both preserve the current URL via ?redirect= so users are
 *     brought straight back here after authenticating.
 *   • Logged in  → shows summary + "Pay now" button which calls
 *     POST /api/payments/checkout/session and forwards to the Stripe URL.
 *
 * Query params:
 *   package = package id (required)
 *   plan    = "weekly" | "full" | "monthly" | "membership" (optional; controls amount)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const currency = (n) => `£${Number(n || 0).toFixed(2)}`;

/** Derive amount + savings displayed based on the selected plan. */
function selectedPlan(pkg, plan) {
  if (!pkg) return { label: "", amount: 0, note: "" };
  if (pkg.period === "month" || plan === "membership") {
    return { label: "Monthly membership", amount: pkg.price, note: "Billed monthly. Cancel anytime per T&Cs.", planId: "membership" };
  }
  const opts = pkg.payment_options || {};
  if (plan === "weekly" && opts.weekly) {
    return {
      label: `${opts.weekly.installments} × ${currency(opts.weekly.amount)} weekly`,
      amount: opts.weekly.amount,
      note: `First instalment charged today. Remaining ${opts.weekly.installments - 1} instalments will be arranged with your clinician.`,
      planId: "weekly",
      total: opts.weekly.total,
    };
  }
  if (plan === "monthly" && opts.monthly) {
    return {
      label: `${opts.monthly.installments} × ${currency(opts.monthly.amount)} monthly`,
      amount: opts.monthly.amount,
      note: `First instalment charged today. Remaining ${opts.monthly.installments - 1} instalments will be arranged with your clinician.`,
      planId: "monthly",
      total: opts.monthly.total,
    };
  }
  return {
    label: "Pay in full",
    amount: pkg.price,
    note: "One-time payment — full programme unlocked immediately.",
    planId: "full",
  };
}

export default function CheckoutSummary() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const packageId = params.get("package");
  const plan = params.get("plan") || "full";
  const screen = params.get("screen") || "";
  // Guest checkout state — buyer pays now, we create the account from Stripe
  // once payment lands (`_grant_purchase` in the backend + welcome email).
  const [guestEmail, setGuestEmail] = useState(params.get("email") || "");
  const [guestName, setGuestName] = useState(params.get("name") || "");

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  // iter100 · reward-code redemption UI state
  const [rewardCode, setRewardCode] = useState("");
  const [rewardApplied, setRewardApplied] = useState(null); // { tier, discount_pct, label }
  const [rewardError, setRewardError] = useState("");
  const [validatingReward, setValidatingReward] = useState(false);
  const [credit, setCredit] = useState(null); // Run B · £95 assessment credit eligibility
  const isMassage = pkg && String(pkg.id || "").startsWith("srv_") &&
    /massage/i.test(pkg.__cat || pkg.duration || pkg.headline || "");

  useEffect(() => {
    if (!packageId) { setLoading(false); return; }
    // À la carte "Book" links use `srv_<service_id>` so we can distinguish a
    // one-off session purchase from a membership / package / online-programme
    // (which live in db.packages). Fetch from the matching source.
    if (packageId.startsWith("srv_")) {
      const svcId = packageId.slice(4);
      api.get("/services/public")
        .then((r) => {
          const all = [];
          for (const c of (r.data?.categories || [])) {
            for (const s of (c.services || [])) all.push({ ...s, __cat: c.slug });
          }
          const s = all.find((x) => x.id === svcId);
          if (!s) return setPkg(null);
          setPkg({
            id: `srv_${s.id}`, name: s.name, price: s.price,
            headline: s.description, duration: `${s.duration_min || 60} min · ${s.category || ""}`.trim(),
            __cat: s.__cat || s.category || "",
            features: [
              `${s.duration_min || 60}-minute session`,
              "In-clinic or (where offered) home visit",
              "Book any available slot after checkout",
            ],
          });
        })
        .finally(() => setLoading(false));
      return;
    }
    api.get("/packages")
      .then((r) => setPkg((r.data || []).find((p) => p.id === packageId) || null))
      .finally(() => setLoading(false));
  }, [packageId]);

  const sel = useMemo(() => selectedPlan(pkg, plan), [pkg, plan]);
  const isMembership2 = pkg && (pkg.period === "month" || plan === "membership");
  const creditAmt = (credit?.eligible && pkg && (pkg.id === "progress" || pkg.id === "performance") && isMembership2) ? 95 : 0;
  const dueToday = Math.max(0, (sel.amount || 0) - creditAmt);

  useEffect(() => {
    if (!user || !pkg) return;
    if (isMembership2 && (pkg.id === "progress" || pkg.id === "performance")) {
      api.get("/promo/assessment-credit").then((r) => setCredit(r.data)).catch(() => setCredit(null));
    }
  }, [user, pkg, plan, isMembership2]);
  const redirectHere = `/checkout/summary?package=${packageId}&plan=${plan}${screen ? `&screen=${encodeURIComponent(screen)}` : ""}`;

  const applyReward = async () => {
    const code = (rewardCode || "").trim().toUpperCase();
    if (!code) return;
    setRewardError("");
    setValidatingReward(true);
    try {
      const { data } = await api.post("/me/rewards/validate", { code, package_id: packageId });
      setRewardApplied(data);
      toast.success(`${data.discount_pct}% off applied — ${data.tier.toUpperCase()} reward`);
    } catch (e) {
      setRewardApplied(null);
      setRewardError(e?.response?.data?.detail || "Could not apply this code.");
    } finally {
      setValidatingReward(false);
    }
  };

  const removeReward = () => { setRewardApplied(null); setRewardCode(""); setRewardError(""); };

  const payNow = async () => {
    if (!user) {
      nav(`/register?redirect=${encodeURIComponent(redirectHere)}`);
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: packageId,
        plan: sel.planId,
        origin_url: window.location.origin,
        screen: screen || undefined,
        reward_code: rewardApplied ? rewardCode.trim().toUpperCase() : undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setPaying(false);
    }
  };

  const guestPay = async () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());
    if (!emailOk) { toast.error("Please enter a valid email address."); return; }
    if (!guestName.trim()) { toast.error("Please enter your full name."); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/guest-session", {
        package_id: packageId,
        plan: sel.planId,
        origin_url: window.location.origin,
        screen: screen || undefined,
        email: guestEmail.trim(),
        name: guestName.trim(),
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="fl-page min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 flex items-center gap-3 text-zinc-400">
          <Loader2 size={16} className="animate-spin" /> Loading your selection…
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="fl-page min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-center" data-testid="checkout-not-found">
          <h1 className="fl-heading text-3xl">Package not found</h1>
          <p className="text-zinc-400 mt-3">The plan you selected is no longer available. Please choose another package.</p>
          <Link to="/prices" className="fl-btn mt-6 inline-flex" data-testid="checkout-back-to-prices">
            <ArrowLeft size={14} /> Back to pricing
          </Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="fl-page min-h-screen">
      <PublicHeader />

      <section className="pt-28 pb-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/prices" className="text-xs text-zinc-500 hover:text-[#f47a20] inline-flex items-center gap-1" data-testid="checkout-back">
            <ArrowLeft size={12} /> Back to pricing
          </Link>
          <div className="fl-label text-[#f47a20] mt-4">Your selection</div>
          <h1 className="fl-heading text-4xl md:text-5xl mt-2">Confirm & pay</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Review your package below. {user ? "When you're ready, click Pay now to complete your purchase securely with Stripe." : "Create an account or sign in — you'll come straight back here to pay."}
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-5 gap-6">
          {/* Package summary */}
          <div className="md:col-span-3 fl-card p-6" data-testid="checkout-summary-card">
            <div className="fl-label text-[#f47a20]">{pkg.type === "membership" || pkg.period === "month" ? "Membership" : "Package"}</div>
            <div className="fl-heading text-2xl mt-2">{pkg.name}</div>
            {pkg.tagline && <div className="text-sm text-zinc-400 italic mt-1">{pkg.tagline}</div>}
            {pkg.duration && (
              <div className="text-xs text-zinc-500 mt-2">Recommended duration: <b className="text-zinc-300">{pkg.duration}</b></div>
            )}

            <div className="fl-label mt-6 mb-2">What's included</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              {((pkg.features && pkg.features.length > 0)
                  ? pkg.features
                  : (pkg.card_highlights || [])
                ).map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={14} className="text-[#f47a20] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Cart / total */}
          <div className="md:col-span-2 fl-card p-6 h-max sticky top-24" data-testid="checkout-total-card">
            <div className="fl-label">Order summary</div>
            <div className="mt-4 flex justify-between text-sm text-zinc-300">
              <span>{pkg.name}</span>
              <span className="text-zinc-500">{sel.label}</span>
            </div>
            {creditAmt > 0 && (
              <div className="mt-2 flex justify-between text-sm text-emerald-400" data-testid="checkout-assessment-credit">
                <span>£95 assessment credit</span>
                <span>−{currency(creditAmt)}</span>
              </div>
            )}

            <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--fl-border)" }}>
              <div className="flex items-baseline justify-between">
                <span className="fl-label">Due today</span>
                <span className="fl-heading text-4xl text-[#f47a20]" data-testid="checkout-amount">{currency(dueToday)}</span>
              </div>
              {sel.total && sel.total !== sel.amount && (
                <div className="text-xs text-zinc-500 mt-1 text-right">Programme total {currency(sel.total)}</div>
              )}
              <div className="text-xs text-zinc-500 mt-3">{sel.note}</div>
              {creditAmt > 0 && (
                <div className="text-xs text-zinc-400 mt-1" data-testid="checkout-recurring-note">then {currency(sel.amount)}/month</div>
              )}
            </div>

            {/* iter100 · reward-code redemption — patient-only, massage-only */}
            {user && isMassage && (
              <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--fl-border)" }} data-testid="reward-code-block">
                <div className="fl-label mb-2 flex items-center gap-2">
                  Have a reward code?
                  <span className="text-[10px] text-zinc-500 normal-case tracking-normal">bronze / silver / gold</span>
                </div>
                {!rewardApplied ? (
                  <>
                    <div className="flex gap-2">
                      <input
                        className="fl-input font-mono uppercase"
                        placeholder="FL-B-XXXXXX"
                        value={rewardCode}
                        onChange={(e) => { setRewardCode(e.target.value.toUpperCase()); setRewardError(""); }}
                        maxLength={16}
                        data-testid="reward-code-input"
                      />
                      <button
                        type="button"
                        onClick={applyReward}
                        disabled={validatingReward || !rewardCode.trim()}
                        className="fl-btn text-sm shrink-0"
                        data-testid="reward-code-apply"
                      >
                        {validatingReward ? "…" : "Apply"}
                      </button>
                    </div>
                    {rewardError && <div className="text-xs text-red-400 mt-2" data-testid="reward-code-error">{rewardError}</div>}
                  </>
                ) : (
                  <div className="flex items-center justify-between p-3 rounded-md" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }} data-testid="reward-code-applied">
                    <div>
                      <div className="text-xs font-semibold text-emerald-400 uppercase">
                        {rewardApplied.tier} · {rewardApplied.discount_pct}% off
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5 font-mono">{rewardCode}</div>
                    </div>
                    <button
                      type="button"
                      onClick={removeReward}
                      className="text-xs text-zinc-400 hover:text-white"
                      data-testid="reward-code-remove"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <div className="text-[10px] text-zinc-500 mt-2">
                  Discount is applied at Stripe checkout. Single-use per tier.
                </div>
              </div>
            )}

            {user ? (
              <button
                onClick={payNow}
                disabled={paying}
                className="fl-btn-anim fl-btn w-full justify-center mt-6"
                data-testid="checkout-pay-now"
              >
                {paying ? <><Loader2 size={14} className="animate-spin" /> Redirecting to Stripe…</> : <>Pay now {rewardApplied ? `(−${rewardApplied.discount_pct}%)` : currency(dueToday)} <ArrowRight size={14} /></>}
              </button>
            ) : (
              <div className="mt-6 flex flex-col gap-3" data-testid="checkout-guest-block">
                <div className="fl-label text-[#f47a20]">Pay as guest</div>
                <input
                  type="text" placeholder="Full name"
                  className="fl-input"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  data-testid="checkout-guest-name"
                />
                <input
                  type="email" placeholder="Email · we'll send your receipt here"
                  className="fl-input"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  data-testid="checkout-guest-email"
                />
                <button
                  onClick={guestPay}
                  disabled={paying}
                  className="fl-btn-anim fl-btn w-full justify-center"
                  data-testid="checkout-guest-pay"
                >
                  {paying ? <><Loader2 size={14} className="animate-spin" /> Redirecting to Stripe…</>
                          : <>Pay {currency(sel.amount)} <ArrowRight size={14} /></>}
                </button>
                <div className="text-[11px] text-zinc-500 leading-relaxed">
                  We'll auto-create your Functional LAB account from these details and email you a link to set your password. Already have an account?
                  {" "}
                  <Link to={`/login?redirect=${encodeURIComponent(redirectHere)}`} className="text-[#f47a20] hover:underline" data-testid="checkout-login-and-pay">
                    Sign in
                  </Link>
                  .
                </div>
              </div>
            )}

            <div className="mt-4 text-[11px] text-zinc-500 flex items-start gap-1">
              <ShieldCheck size={11} className="mt-0.5 shrink-0" />
              Payments processed securely by Stripe.
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
